import api from '../../services/api'

const state = {
    categories: [],
    posts: [],
    currentPost: null,
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
    loading: false,
    loadingPost: false,
    totalUnread: 0,
    filters: {
        category: null,
        sort: 'newest',
        search: ''
    }
}

const getters = {
    categories: s => s.categories,
    posts: s => s.posts,
    currentPost: s => s.currentPost,
    pagination: s => s.pagination,
    loading: s => s.loading,
    loadingPost: s => s.loadingPost,
    totalUnread: s => s.totalUnread,
    filters: s => s.filters,
    followedCategories: s => s.categories.filter(c => c.isFollowing)
}

const mutations = {
    SET_CATEGORIES(state, { categories, totalUnread }) {
        state.categories = categories
        state.totalUnread = totalUnread || 0
    },
    SET_POSTS(state, { posts, pagination }) {
        state.posts = posts
        state.pagination = pagination
    },
    APPEND_POSTS(state, { posts, pagination }) {
        state.posts = [...state.posts, ...posts]
        state.pagination = pagination
    },
    SET_CURRENT_POST(state, post) {
        state.currentPost = post
    },
    SET_LOADING(state, val) {
        state.loading = val
    },
    SET_LOADING_POST(state, val) {
        state.loadingPost = val
    },
    SET_FILTERS(state, filters) {
        state.filters = { ...state.filters, ...filters }
    },
    UPDATE_POST_REACTION(state, { postId, reactionType, action }) {
        const post = state.posts.find(p => p.postId === postId)
        if (post) {
            post.userReaction = reactionType
            if (action === 'added') post.reactionCount++
            else if (action === 'removed') post.reactionCount = Math.max(0, post.reactionCount - 1)
        }
        if (state.currentPost && state.currentPost.postId === postId) {
            state.currentPost.userReaction = reactionType
            if (action === 'added') state.currentPost.reactionCount++
            else if (action === 'removed') state.currentPost.reactionCount = Math.max(0, state.currentPost.reactionCount - 1)
        }
    },
    ADD_COMMENT(state, comment) {
        if (!state.currentPost) return
        if (comment.parentId) {
            const parent = state.currentPost.comments.find(c => c.commentId === comment.parentId)
            if (parent) {
                if (!parent.replies) parent.replies = []
                parent.replies.push(comment)
            }
        } else {
            comment.replies = []
            state.currentPost.comments.push(comment)
        }
        state.currentPost.commentCount++
    },
    REMOVE_COMMENT(state, commentId) {
        if (!state.currentPost) return
        // Check top-level
        const idx = state.currentPost.comments.findIndex(c => c.commentId === commentId)
        if (idx !== -1) {
            state.currentPost.comments.splice(idx, 1)
            state.currentPost.commentCount--
            return
        }
        // Check replies
        for (const parent of state.currentPost.comments) {
            const rIdx = (parent.replies || []).findIndex(r => r.commentId === commentId)
            if (rIdx !== -1) {
                parent.replies.splice(rIdx, 1)
                state.currentPost.commentCount--
                return
            }
        }
    },
    TOGGLE_CATEGORY_FOLLOW(state, { slug, isFollowing }) {
        const cat = state.categories.find(c => c.slug === slug)
        if (cat) {
            cat.isFollowing = isFollowing
            if (isFollowing) cat.unreadCount = 0
        }
    }
}

const actions = {
    async fetchCategories({ commit }) {
        try {
            const { data } = await api.get('/blog/categories')
            commit('SET_CATEGORIES', data)
        } catch (error) {
            console.error('Failed to fetch blog categories:', error)
        }
    },

    async fetchPosts({ commit, state }, { page = 1, append = false } = {}) {
        commit('SET_LOADING', true)
        try {
            const params = { page, limit: state.pagination.limit }
            if (state.filters.category) params.category = state.filters.category
            if (state.filters.sort) params.sort = state.filters.sort
            if (state.filters.search) params.search = state.filters.search

            const { data } = await api.get('/blog', { params })

            if (append) {
                commit('APPEND_POSTS', data)
            } else {
                commit('SET_POSTS', data)
            }
        } catch (error) {
            console.error('Failed to fetch blog posts:', error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async fetchPost({ commit }, idOrSlug) {
        commit('SET_LOADING_POST', true)
        try {
            const { data } = await api.get(`/blog/${idOrSlug}`)
            commit('SET_CURRENT_POST', data.post)
            return data.post
        } catch (error) {
            console.error('Failed to fetch blog post:', error)
            throw error
        } finally {
            commit('SET_LOADING_POST', false)
        }
    },

    async createPost(_, postData) {
        const { data } = await api.post('/blog', postData)
        return data
    },

    async updatePost(_, { postId, ...postData }) {
        const { data } = await api.put(`/blog/${postId}`, postData)
        return data
    },

    async deletePost({ commit }, postId) {
        await api.delete(`/blog/${postId}`)
        commit('SET_CURRENT_POST', null)
    },

    async toggleReaction({ commit }, { postId, reactionType, commentId }) {
        const { data } = await api.post(`/blog/${postId}/reactions`, { reactionType, commentId })
        if (!commentId) {
            commit('UPDATE_POST_REACTION', { postId, reactionType: data.reactionType, action: data.action })
        }
        return data
    },

    async addComment({ commit }, { postId, body, parentId, quotedText, mentionedUserIds }) {
        const { data } = await api.post(`/blog/${postId}/comments`, {
            body, parentId, quotedText, mentionedUserIds
        })
        commit('ADD_COMMENT', data.comment)
        return data.comment
    },

    async editComment(_, { postId, commentId, body }) {
        await api.put(`/blog/${postId}/comments/${commentId}`, { body })
    },

    async deleteComment({ commit }, { postId, commentId }) {
        await api.delete(`/blog/${postId}/comments/${commentId}`)
        commit('REMOVE_COMMENT', commentId)
    },

    async toggleFollow({ commit }, slug) {
        const cat = state.categories.find(c => c.slug === slug)
        const action = cat?.isFollowing ? 'unfollow' : 'follow'
        await api.post(`/blog/categories/${slug}/follow`, { action })
        commit('TOGGLE_CATEGORY_FOLLOW', { slug, isFollowing: action === 'follow' })
    },

    async markCategorySeen(_, slug) {
        await api.post(`/blog/categories/${slug}/follow`, { action: 'seen' })
    },

    async flagContent(_, { postId, commentId, reason, details }) {
        await api.post('/blog/flag', { postId, commentId, reason, details })
    },

    setFilters({ commit, dispatch }, filters) {
        commit('SET_FILTERS', filters)
        dispatch('fetchPosts')
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
